/**
 * Audit.tsx — tamper-evident audit viewer (SPEC §8).
 *
 * The audit log is a per-org SHA-256 hash chain. We render events with their
 * prev_hash→hash linkage and run a client-side chain verification so a break
 * (tampering) is visible. The authoritative verification is server-side; this
 * is a defense-in-depth display check (SPEC §8: "a break = tampering").
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useApi } from '@/api/provider';

export default function Audit() {
  const api = useApi();
  const [action, setAction] = useState('');

  // GET /audit returns { events, verify }: the events plus the SERVER's
  // authoritative hash-chain verification (SPEC §8). The server computes hashes
  // over the canonical form and emits them as hex; we trust verify.ok rather than
  // recomputing client-side (which can't reproduce the server's canonical bytes).
  const { data, isLoading } = useQuery({
    queryKey: ['audit', action],
    queryFn: () => api.listAudit(action ? { action } : {}),
  });
  const events = data?.events ?? [];
  const verification: ChainState = data
    ? data.verify.ok
      ? events.length === 0
        ? 'empty'
        : 'intact'
      : 'broken'
    : 'checking';

  return (
    <div className="p-6 sm:p-8">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="flex items-center gap-2.5">
            <h1 className="text-xl font-bold text-helm-text">Audit Log</h1>
            <ChainStatus state={verification} />
          </div>
          <p className="mt-0.5 text-sm text-helm-muted">
            Tamper-evident event log secured by a per-org SHA-256 hash chain.
          </p>
        </div>
        <select value={action} onChange={(e) => setAction(e.target.value)} className="rh-input w-auto">
          <option value="">All actions</option>
          {ACTIONS.map((a) => (
            <option key={a} value={a}>
              {a}
            </option>
          ))}
        </select>
      </div>

      <div className="rh-card overflow-hidden">
        <table className="rh-table">
          <thead>
            <tr>
              <th>Time</th>
              <th>Actor</th>
              <th>Action</th>
              <th>Target</th>
              <th>Hash</th>
            </tr>
          </thead>
          <tbody>
            {events.map((e) => (
              <tr key={e.id}>
                <td className="whitespace-nowrap text-helm-muted">{new Date(e.ts).toLocaleString()}</td>
                <td className="font-mono text-xs text-helm-text">{e.actor}</td>
                <td className="font-medium text-helm-text">{e.action}</td>
                <td className="font-mono text-xs text-helm-muted">{e.target || '—'}</td>
                <td className="font-mono text-[10px] text-helm-muted" title={e.hash}>
                  {e.hash.slice(0, 12)}…
                </td>
              </tr>
            ))}
            {isLoading && (
              <tr>
                <td colSpan={5} className="py-10 text-center text-helm-muted">
                  Loading audit events…
                </td>
              </tr>
            )}
            {events.length === 0 && !isLoading && (
              <tr>
                <td colSpan={5} className="py-12 text-center text-helm-muted">
                  No audit events.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const ACTIONS = [
  'session.requested',
  'session.started',
  'session.ended',
  'consent.granted',
  'consent.denied',
  'auth.login',
  'auth.fail',
  'device.enrolled',
  'device.revoked',
  'file.sent',
  'file.received',
  'shell.opened',
  'installer.created',
  'code.created',
  'code.redeemed',
];

type ChainState = 'checking' | 'intact' | 'broken' | 'empty';

function ChainStatus({ state }: { state: ChainState }) {
  const map: Record<ChainState, { label: string; badge: string; dot: string }> = {
    checking: {
      label: 'Verifying chain…',
      badge: 'bg-helm-elevated text-helm-muted',
      dot: 'bg-helm-muted',
    },
    intact: {
      label: 'Chain intact',
      badge: 'bg-helm-good/15 text-helm-good',
      dot: 'bg-helm-good',
    },
    broken: {
      label: 'Chain broken — possible tampering',
      badge: 'bg-helm-bad/15 text-helm-bad',
      dot: 'bg-helm-bad',
    },
    empty: {
      label: 'No events',
      badge: 'bg-helm-elevated text-helm-muted',
      dot: 'bg-helm-muted',
    },
  };
  const { label, badge, dot } = map[state];
  return (
    <span className={`rh-badge ${badge}`} title="Hash-chain verification status">
      <span className={`h-1.5 w-1.5 rounded-full ${dot}`} />
      {label}
    </span>
  );
}
